import { motion } from 'framer-motion';
import { X, Navigation, Rocket } from 'lucide-react';
import { useCountdown } from '@/hooks/use-countdown';
import type { UpcomingLaunch } from '@vtak/mod-space/client';
import { LaunchCrew } from './launch-crew';
import { LaunchBoosters } from './launch-booster';
import { WebcastEmbed } from './webcast-embed';

/** Detailed launch modal with imagery, full info, crew, recovery, and webcast. */
export function LaunchDetailModal({
  launch,
  onClose,
  onGoToLocation,
}: {
  launch: UpcomingLaunch;
  onClose: () => void;
  onGoToLocation: (launch: UpcomingLaunch) => void;
}): React.JSX.Element {
  const hasPad = launch.lat !== null && launch.lon !== null;
  const { h, m, s, past } = useCountdown(launch.net || launch.windowStart);

  return (
    <div
      className="absolute inset-0 z-[10] flex items-center justify-center bg-black/60 p-6 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.16, ease: 'easeOut' }}
        onClick={(event) => event.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-echo-border bg-echo-grey shadow-2xl ring-1 ring-black/30"
      >
        <ModalHeader launch={launch} onClose={onClose} />

        <div className="min-h-0 flex-1 overflow-y-auto">
          {launch.image && <img src={launch.image} alt="" className="max-h-56 w-full object-cover" />}

          <div className="space-y-1 px-4 py-3">
            <Row label="Vehicle" value={launch.name} />
            <Row label="Rocket" value={launch.rocket} />
            <Row label="Provider" value={launch.provider} />
            <Row label="Program" value={launch.program} />
            <Row label="Type" value={launch.missionType} />
            <Row label="Orbit" value={launch.orbit} />
            <Row label="Pad" value={launch.padName} />
            <Row label="Location" value={launch.location} />
            <StatusRow status={launch.status} probability={launch.probability} />
            <Row label="Liftoff" value={formatFull(launch.net)} />
            <Row label="T-minus" value={past ? 'Launched' : `${pad(h)}:${pad(m)}:${pad(s)}`} />
          </div>

          {launch.description && (
            <div className="border-t border-echo-border px-4 py-3">
              <div className="mb-1 text-[11px] uppercase tracking-wider text-muted-foreground">Mission</div>
              <p className="text-sm leading-relaxed text-foreground/90">{launch.description}</p>
            </div>
          )}

          <LaunchCrew crew={launch.crew} destination={launch.destination} spacecraft={launch.spacecraft} />
          <LaunchBoosters boosters={launch.boosters} />
          <WebcastEmbed url={launch.webcastUrl} live={launch.webcastLive} />
        </div>

        {hasPad && (
          <button
            type="button"
            onClick={() => onGoToLocation(launch)}
            className="flex w-full shrink-0 items-center justify-center gap-2 border-t border-echo-border bg-echo-yellow py-2.5 text-xs font-semibold uppercase tracking-wider text-black transition-colors hover:bg-echo-yellow/90"
          >
            <Navigation className="h-3.5 w-3.5" />
            Go to Location
          </button>
        )}
      </motion.div>
    </div>
  );
}

/** Modal header with mission patch, title, provider logo, and close. */
function ModalHeader({ launch, onClose }: { launch: UpcomingLaunch; onClose: () => void }): React.JSX.Element {
  return (
    <div className="flex shrink-0 items-center gap-2 border-b border-echo-border px-4 py-3">
      {launch.patch ? (
        <img src={launch.patch} alt="" className="h-7 w-7 shrink-0 object-contain" />
      ) : (
        <Rocket className="h-4 w-4 shrink-0 text-echo-yellow" />
      )}
      <h2 className="flex-1 truncate font-chakra text-sm font-semibold uppercase tracking-wider text-foreground">
        {launch.mission || launch.name}
      </h2>
      {launch.providerLogo && (
        <img src={launch.providerLogo} alt="" className="h-5 max-w-20 shrink-0 object-contain opacity-90" />
      )}
      <button type="button" onClick={onClose} aria-label="Close" className="text-muted-foreground hover:text-foreground">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

/** Status row with an optional go-probability chip. */
function StatusRow({ status, probability }: { status: string; probability: number | null }): React.JSX.Element | null {
  if (!status) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Status</span>
      <span className="flex items-center gap-2 text-right text-sm text-foreground">
        {status}
        {probability !== null && (
          <span className="rounded-full bg-green-500/20 px-2 py-0.5 text-[10px] font-semibold text-green-400">
            GO {probability}%
          </span>
        )}
      </span>
    </div>
  );
}

/** A label/value detail row (hidden when empty). */
function Row({ label, value }: { label: string; value: string }): React.JSX.Element | null {
  if (!value) return null;
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</span>
      <span className="truncate text-right text-sm text-foreground">{value}</span>
    </div>
  );
}

/** Zero-pad to two digits. */
function pad(value: number): string {
  return String(value).padStart(2, '0');
}

/** Full local date-time. */
function formatFull(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
